<template>
  <v-dialog
    :value="!!recordId"
    @input="handleClose"
    max-width="1200"
    content-class="page-view-dialog"
  >
    <modal-card
      v-if="recordId"
      :title="pageTitle"
      fixed
      full
      :loading="loading"
      @close="handleClose"
    >
      <!-- Header actions slot -->
      <template #headerActions>
        <v-btn icon class="mr-2" @click="handleClose">
          <v-icon color="light">close</v-icon>
        </v-btn>
      </template>

      <!-- Page content -->
      <div class="page-view-modal__content">
        <!-- Error state -->
        <div v-if="loadError" class="text-center pa-8">
          <v-icon size="48" color="error" class="mb-4">
            mdi-alert-circle-outline
          </v-icon>
          <p class="text-body-1 error--text">{{ loadError }}</p>
        </div>

        <!-- Page renderer -->
        <div v-else-if="currentPageId" class="page-view-modal__renderer">
          <page-renderer :page-id="currentPageId" :edit-mode="false" />
        </div>

        <!-- Empty state -->
        <div v-else-if="!loading" class="text-center pa-8">
          <v-icon size="48" color="grey lighten-1" class="mb-4">
            mdi-file-document-outline
          </v-icon>
          <p class="text-body-1 grey--text">This page has no content yet.</p>
        </div>
      </div>
    </modal-card>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import PageRenderer from '@/components/pages/PageRenderer.vue';

export default {
  name: 'PageViewModal',
  components: { PageRenderer },
  props: {
    /**
     * The record ID of the page to display.
     * Pass null/undefined to close the modal.
     */
    recordId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      loadError: null,
      currentPageId: null,
    };
  },
  computed: {
    ...mapState('pages', ['recordsById']),
    ...mapGetters('pages', ['recordById']),

    currentRecord() {
      if (!this.recordId) return null;
      return this.recordById(this.recordId);
    },

    pageTitle() {
      if (this.currentRecord) {
        return this.currentRecord.name || 'Untitled Page';
      }
      return 'Page';
    },
  },
  watch: {
    recordId: {
      immediate: true,
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.loadPage();
        } else if (!newId) {
          this.cleanup();
        }
      },
    },
  },
  methods: {
    ...mapActions('pages', ['fetchRecord', 'loadRecordPage', 'deletePage']),

    async loadPage() {
      if (!this.recordId) return;

      this.loading = true;
      this.loadError = null;
      this.currentPageId = null;

      try {
        // Fetch the record from API
        const { ok, data } = await this.fetchRecord({
          recordId: this.recordId,
        });

        if (!ok || !data) {
          this.loadError = 'Page not found or could not be loaded.';
          return;
        }

        // Load the page content into memory for rendering
        // Note: Vuex actions always return Promises, so we must await
        const pageId = await this.loadRecordPage({ recordId: this.recordId });

        if (!pageId) {
          this.loadError = 'Page has no content to display.';
          return;
        }

        this.currentPageId = pageId;
      } catch (err) {
        console.error('[PageViewModal] Failed to load page:', err);
        this.loadError = 'An error occurred while loading the page.';
      } finally {
        this.loading = false;
      }
    },

    handleClose() {
      this.$emit('close');
    },

    cleanup() {
      // Clean up the in-memory page when modal closes
      if (this.currentPageId) {
        this.deletePage({ pageId: this.currentPageId });
        this.currentPageId = null;
      }
      this.loadError = null;
    },
  },
  beforeDestroy() {
    this.cleanup();
  },
};
</script>

<style lang="less">
.page-view-dialog {
  // Make dialog nearly full screen
  height: 90vh !important;
  max-height: 90vh !important;

  .styled-modal {
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: #fff;
  }

  .styled-modal__content {
    background-color: #fff;
  }

  .fixed-modal__content--full {
    flex: 1;
    overflow-y: auto;
    padding: 0 !important;
    background-color: #fff;
  }
}

.page-view-modal {
  &__content {
    min-height: 300px;
    height: 100%;
    background-color: #fff;
  }

  &__renderer {
    padding: 24px;
    max-width: 800px;
    margin: 0 auto;
    background-color: #fff;
    min-height: 100%;
  }
}
</style>
